import { AppConstants } from "@/helpers/app.constants";
import { AlertService } from "@/services/alert.service";
import { AppConfigService } from "@/services/app-config.service";
import { EmailWithTLDValidator } from "@/validators/EmailWithTLDValidator";
import { NgIf, NgTemplateOutlet } from "@angular/common";
import { Component, OnDestroy, OnInit, Renderer2 } from "@angular/core";
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { UsersService } from "@eemo/api-client";
import { ReCaptchaV3Service } from "ng-recaptcha";
import { Subscription } from "rxjs";
import { environment } from "src/environments/environment";
import { CardBaseComponent } from "../../../components/base/card-base/card-base.component";

@Component({
  templateUrl: "./password-lost.component.html",
  styleUrls: ["./password-lost.component.scss"],
  standalone: true,
  imports: [
    CardBaseComponent,
    NgTemplateOutlet,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    NgIf,
    MatButtonModule,
    RouterLink,
  ],
})
export class PasswordLostComponent implements OnInit, OnDestroy {
  passwordLostForm: UntypedFormGroup;
  loading = false;
  submitted = false;
  returnUrl: string;

  recaptchaSub = new Subscription();
  lostPasswordSub = new Subscription();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private userService: UsersService,
    private alertService: AlertService,
    public appConfigService: AppConfigService,
    private recaptchaV3Service: ReCaptchaV3Service,
    private renderer: Renderer2
  ) {}

  ngOnInit() {
    this.returnUrl =
      this.route.snapshot.queryParams[AppConstants.PARAM_RETURN_URL] || "/";
    if (!this.appConfigService.isMobileApp) {
      this.renderer.addClass(document.body, "recaptcha");
      this.renderer.addClass(document.body, "httpcs");
    }
    this.passwordLostForm = this.formBuilder.group({
      email: [
        this.route.snapshot.queryParams.email,
        [Validators.required, EmailWithTLDValidator],
      ],
    });
  }

  ngOnDestroy() {
    this.renderer.removeClass(document.body, "recaptcha");
    this.renderer.removeClass(document.body, "httpcs");
    this.recaptchaSub.unsubscribe();
    this.lostPasswordSub.unsubscribe();
  }

  // convenience getter for easy access to form fields
  f(field: string): AbstractControl {
    return this.passwordLostForm.get(field);
  }

  onSubmit() {
    // reset alerts on submit
    this.alertService.clear();

    // stop here if form is invalid
    if (this.passwordLostForm.invalid) {
      return;
    }
    if (environment.recaptchaEnabled) {
      this.recaptchaSub = this.recaptchaV3Service
        .execute("lostUserPassword")
        .subscribe({
          next: (token) => {
            this.doPasswordLost(token);
          },
          error: (error) => {
            this.alertService.badRequest(
              "Error trying to verify the ReCaptcha token"
            );
          },
        });
    } else {
      this.doPasswordLost(null);
    }
  }

  getQueryParams(): any {
    const queryParams = {};
    queryParams[AppConstants.PARAM_RETURN_URL] = this.returnUrl;
    return queryParams;
  }

  doPasswordLost(recaptchaToken: string) {
    this.submitted = true;
    this.loading = true;

    this.lostPasswordSub = this.userService
      .lostUserPassword(this.f("email").value, null, recaptchaToken)
      .subscribe({
        next: () => {
          this.alertService.passwordLostSuccess();
          this.loading = false;
          const queryParams = this.getQueryParams();
          this.router.navigate(["/"], { queryParams });
        },
        error: () => {
          this.loading = false;
        },
      });
  }
}
